import { useEffect, useMemo, useState, useRef } from 'react';
import { useLocation, useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Search, Calendar, User, ArrowLeft, ArrowRight, Lock, Plus, Pencil, Trash2, Save, Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { SUPPORTED_LANGS, getBestLanguage } from '../utils/languageStorage';
import { SEO } from './SEO';

const API_BASE_URL = import.meta.env.VITE_APP_SERVER || '';

const LANG_LABELS: Record<string, string> = {
  en: 'English',
  sa: 'Sanskrit',
  cn: 'Chinese (Simplified)',
  zh: 'Chinese (Traditional)',
  de: 'German',
  es: 'Spanish',
  pl: 'Polish',
  hi: 'Hindi',
  ta: 'Tamil',
  te: 'Telugu',
  ne: 'Nepali',
  he: 'Hebrew',
  fr: 'French',
  ja: 'Japanese',
  pt: 'Portuguese',
  id: 'Indonesian',
  ru: 'Russian',
  nl: 'Dutch',
};

type Lang = string;

interface PostTranslation { title: string; content: string; }
interface Post {
  id?: number;
  slug?: string;
  title: string;
  content: string;
  status: 'draft' | 'published';
  language?: string;
  translations?: Record<string, PostTranslation>;
  created_at?: string;
  updated_at?: string;
}

function detectLocale(paramLang?: string): Lang {
  const l = (paramLang || '').toLowerCase();
  if (l === 'jp') return 'ja';
  // Accept any language in SUPPORTED_LANGS, plus 'sa' for Sanskrit
  if (l === 'sa' || SUPPORTED_LANGS.includes(l)) return l;
  // Fall back to best detected language (storage/browser), or default
  return getBestLanguage();
}

function normalizePostFromAPI(api: any): Post {
  const base: Post = {
    id: api.id,
    slug: api.slug,
    title: api.title ?? '',
    content: api.content ?? '',
    status: api.status ?? 'draft',
    language: api.language,
    created_at: api.created_at,
    updated_at: api.updated_at,
    translations: undefined,
  };
  if (Array.isArray(api.translations)) {
    base.translations = {};
    for (const tr of api.translations) {
      const code = (tr.language_code || '').toLowerCase();
      if (code) {
        base.translations[code] = { title: tr.title ?? '', content: tr.content ?? '' };
      }
    }
  }
  return base;
}

type View = 'LIST' | 'POST' | 'ADMIN_LOGIN' | 'ADMIN_DASH' | 'ADMIN_EDIT';

export default function BlogPage({ adminMode }: { adminMode?: boolean }) {
  const { lang: paramLang, slug } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation(['nav', 'seo']);
  const lang = useMemo(() => detectLocale(paramLang), [paramLang]);
  const isAdminRoute = useMemo(() => location.pathname.endsWith('/admin') || Boolean(adminMode), [location.pathname, adminMode]);
  const blogHeading = t('nav:nav.blog', { defaultValue: 'Blog' });
  const blogSeoTitle = blogHeading;
  const blogSeoDescription = t('seo:blog.description', {
    defaultValue: 'Explore Sarvalok articles on Sanatana Dharma, Sanskrit texts, Jyotisha, philosophy, ritual, and source-grounded Indic knowledge.',
  });

  const [posts, setPosts] = useState<Post[]>([]);
  const [view, setView] = useState<View>('LIST');
  const [isAdmin, setIsAdmin] = useState(false);
  const [current, setCurrent] = useState<Post | null>(null);
  const [listLoading, setListLoading] = useState(false);
  const [postLoading, setPostLoading] = useState(false);
  const [listError, setListError] = useState<string | null>(null);
  const [postError, setPostError] = useState<string | null>(null);

  useEffect(() => {
    const listAbort = new AbortController();
    if (isAdminRoute) return () => { listAbort.abort(); };
    setListLoading(true);
    setListError(null);
    fetch(`${API_BASE_URL}/api/blog/posts?status_filter=published&lang=${lang}`, { credentials: 'include', signal: listAbort.signal })
      .then(r => r.ok ? r.json() : Promise.reject(r))
      .then(list => setPosts((list || []).map(normalizePostFromAPI)))
      .catch((err) => {
        if (err?.name === 'AbortError') return;
        setListError('Failed to load posts. Please refresh.');
      })
      .finally(() => setListLoading(false));
    return () => {
      listAbort.abort();
    };
  }, [lang, isAdminRoute]);

  useEffect(() => {
    const adminAbort = new AbortController();
    fetch(`${API_BASE_URL}/api/blog/admin/me`, { credentials: 'include', signal: adminAbort.signal })
      .then(r => r.ok ? r.json() : Promise.reject(r))
      .then(() => { setIsAdmin(true); if (isAdminRoute) setView('ADMIN_DASH'); })
      .catch((err) => {
        if (err?.name === 'AbortError') return;
        setIsAdmin(false);
        if (isAdminRoute) setView('ADMIN_LOGIN');
      });
    return () => {
      adminAbort.abort();
    };
  }, [isAdminRoute]);

  useEffect(() => {
    if (isAdminRoute) return;
    const postAbort = new AbortController();
    if (!slug) {
      setCurrent(null);
      setPostLoading(false);
      setPostError(null);
      setView('LIST');
      return () => { postAbort.abort(); };
    }

    setView('POST');
    setPostLoading(true);
    setPostError(null);

    const optimistic = (location.state as any)?.post;
    if (optimistic?.slug === slug) setCurrent(optimistic);

    fetch(`${API_BASE_URL}/api/blog/posts/${slug}?lang=${lang}`, { credentials: 'include', signal: postAbort.signal })
      .then(r => r.ok ? r.json() : Promise.reject(r))
      .then(p => { setCurrent(normalizePostFromAPI(p)); setView('POST'); })
      .catch((err) => {
        if (err?.name === 'AbortError') return;
        setPostError('Failed to load this post.');
        setCurrent(null);
        setView('LIST');
        navigate(`/${lang}/blog`, { replace: true });
      })
      .finally(() => setPostLoading(false));
    return () => {
      postAbort.abort();
    };
  }, [lang, slug, navigate, isAdminRoute, location.state]);

  const openPost = (p: Post) => {
    setCurrent(p);
    setPostLoading(true);
    setPostError(null);
    setView('POST');
    if (p.slug) navigate(`/${lang}/blog/${p.slug}`, { state: { post: p } });
    else { setCurrent(p); setView('POST'); }
  };

  // Navigation helpers for prev/next within current language list
  const currentIndex = current ? posts.findIndex(x => x.id === current.id) : -1;
  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex >= 0 && currentIndex < posts.length - 1;
  const goPrev = () => { if (hasPrev) { const prev = posts[currentIndex - 1]; setCurrent(prev); setPostLoading(true); if (prev.slug) navigate(`/${lang}/blog/${prev.slug}`, { state: { post: prev } }); } };
  const goNext = () => { if (hasNext) { const next = posts[currentIndex + 1]; setCurrent(next); setPostLoading(true); if (next.slug) navigate(`/${lang}/blog/${next.slug}`, { state: { post: next } }); } };

  return (
    <div style={{ maxWidth: 960, margin: '40px auto', padding: '0 16px' }}>
      {!isAdminRoute && view === 'LIST' && (
        <SEO title={blogSeoTitle} description={blogSeoDescription} url="/blog" />
      )}
      <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1 style={{ margin: 0 }}>
          <Link to={`/${lang}/blog`} style={{ textDecoration: 'none', color: 'inherit' }}>
            Sarvalok {blogHeading}
          </Link>
        </h1>
        <div>
          {isAdmin ? (
            <button onClick={() => setView('ADMIN_DASH')}>Admin</button>
          ) : (
            <a href={`/${lang}/blog/admin`} style={{ textDecoration: 'none' }}>Admin</a>
          )}
        </div>
      </header>

      {view === 'LIST' && (
        <BlogList lang={lang} posts={posts} onOpen={openPost} loading={listLoading} error={listError} />
      )}
      {view === 'POST' && (
        current ? (
          <BlogPostView lang={lang} post={current} onBack={() => navigate(`/${lang}/blog`)} onPrev={goPrev} onNext={goNext} hasPrev={hasPrev} hasNext={hasNext} loading={postLoading} error={postError} />
        ) : (
          <div className="max-w-3xl mx-auto px-4 py-16 flex items-center justify-center gap-2 text-slate-500 dark:text-slate-400">
            <Loader2 className="w-5 h-5 animate-spin" />
            <span>Loading article...</span>
          </div>
        )
      )}
      {view === 'ADMIN_LOGIN' && (
        <AdminLogin onSuccess={() => { setIsAdmin(true); setView('ADMIN_DASH'); }} />
      )}
      {view === 'ADMIN_DASH' && (
        <AdminDashboard lang={lang} posts={posts} setPosts={setPosts} onEdit={(p) => { setCurrent(p); setView('ADMIN_EDIT'); }} />
      )}
      {view === 'ADMIN_EDIT' && current && (
        <AdminEditor post={current} onCancel={() => setView('ADMIN_DASH')} onSaved={(p) => {
          setView('ADMIN_DASH');
          setCurrent(null);
          setPosts(prev => {
            const exists = prev.find(x => x.id === p.id);
            if (exists) return prev.map(x => x.id === p.id ? p : x);
            return [p, ...prev];
          });
        }} />
      )}
    </div>
  );
}

function BlogList({ lang, posts, onOpen, loading, error }: { lang: Lang; posts: Post[]; onOpen: (p: Post) => void; loading: boolean; error: string | null }) {
  const [searchTerm, setSearchTerm] = useState('');
  const filtered = useMemo(() => {
    const s = searchTerm.trim().toLowerCase();
    if (!s) return posts;
    return posts.filter(p =>
      (p.title || '').toLowerCase().includes(s) ||
      (p.content || '').toLowerCase().includes(s)
    );
  }, [posts, searchTerm]);

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="max-w-7xl mx-auto px-4 py-12 md:py-20">
      <div className="text-center max-w-3xl mx-auto mb-12">
        <h1 className="text-4xl md:text-5xl font-extrabold text-slate-900 dark:text-white tracking-tight mb-3">Sarvalok Blog</h1>
        <p className="text-slate-600 dark:text-slate-400">Language: <span className="font-mono">{lang}</span></p>
      </div>

      <div className="mt-6 relative max-w-xl mx-auto group mb-10">
        <div className="absolute -inset-1 bg-gradient-to-r from-indigo-500 to-violet-500 rounded-2xl blur opacity-20 group-hover:opacity-30 transition duration-1000 group-hover:duration-200" />
        <div className="relative flex items-center gap-3 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl px-4 py-3 shadow-sm">
          <Search className="w-5 h-5 text-slate-400" />
          <input
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            placeholder="Search articles..."
            className="flex-1 bg-transparent outline-none text-slate-700 dark:text-slate-200 placeholder-slate-400"
          />
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center gap-2 text-slate-500 dark:text-slate-400">
          <Loader2 className="w-4 h-4 animate-spin" />
          <span>Loading posts...</span>
        </div>
      ) : error ? (
        <div className="text-center text-slate-500 dark:text-slate-400">{error}</div>
      ) : filtered.length === 0 ? (
        <div className="text-center text-slate-500 dark:text-slate-400">No posts yet.</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map(p => {
            const dt = p.updated_at || p.created_at || '';
            let dateLabel = '';
            try { dateLabel = dt ? format(new Date(dt), 'MMM d, yyyy') : ''; } catch {}
            const teaser = (p.content || '').replace(/<[^>]+>/g, '').slice(0, 160);
            return (
              <motion.article key={p.id} whileHover={{ y: -4 }} onClick={() => onOpen(p)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onOpen(p); }} role="button" tabIndex={0} className="group rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-5 shadow-sm hover:shadow-md transition cursor-pointer focus:outline-none focus:ring-2 focus:ring-indigo-500">
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2 line-clamp-2">{p.title}</h3>
                <p className="text-sm text-slate-600 dark:text-slate-400 line-clamp-3 mb-4">{teaser}</p>
                <div className="flex items-center justify-between text-xs text-slate-500">
                  <div className="flex items-center gap-3">
                    {dateLabel && (<span className="flex items-center gap-1"><Calendar className="w-4 h-4" />{dateLabel}</span>)}
                    <span className="flex items-center gap-1"><User className="w-4 h-4" />{lang}</span>
                  </div>
                  <span className="text-indigo-600 group-hover:text-indigo-500 font-medium">Read</span>
                </div>
              </motion.article>
            );
          })}
        </div>
      )}
    </motion.div>
  );
}

function BlogPostView({ lang, post, onBack, onPrev, onNext, hasPrev, hasNext, loading, error }: { lang: Lang; post: Post; onBack: () => void; onPrev: () => void; onNext: () => void; hasPrev: boolean; hasNext: boolean; loading: boolean; error: string | null }) {
  const text = useMemo(() => {
    if (post.language === 'en') return post.content;
    const tr = post.translations?.[lang] || post.translations?.[post.language as Lang];
    return tr?.content || post.content;
  }, [post, lang]);
  const desc = (text || '').replace(/<[^>]+>/g, '').slice(0, 160);
  const canonicalPath = post.slug ? `/${lang}/blog/${post.slug}` : `/${lang}/blog`;
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: post.title,
    datePublished: post.created_at,
    dateModified: post.updated_at,
    inLanguage: lang,
  };
  const noIndex = post.status !== 'published';
  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      <SEO title={post.title} description={desc} url={canonicalPath} type="article" schema={schema} noIndex={noIndex} />
      <button onClick={onBack} className="flex items-center gap-2 text-slate-600 hover:text-slate-900 dark:text-slate-300 dark:hover:text-white mb-4">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>
      <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-3">{post.title}</h2>
      <div className="text-xs text-slate-500 mb-6">Lang: <span className="font-mono">{lang}</span></div>
      {loading && (
        <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400 mb-4">
          <Loader2 className="w-4 h-4 animate-spin" />
          <span>Loading article...</span>
        </div>
      )}
      {!loading && error && (
        <div className="text-sm text-slate-500 dark:text-slate-400 mb-4">{error}</div>
      )}
      <article className="prose prose-slate dark:prose-invert max-w-none">
        <div dangerouslySetInnerHTML={{ __html: text }} />
      </article>
      <div className="mt-8 flex justify-between items-center border-t border-slate-200 dark:border-slate-800 pt-6">
        <button disabled={!hasPrev} onClick={onPrev} className={`${hasPrev ? 'text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800' : 'text-slate-400 cursor-not-allowed'} flex items-center gap-2 px-4 py-2 rounded-md`} aria-disabled={!hasPrev}>
          <ArrowLeft className="w-4 h-4" />
          <span>Previous</span>
        </button>
        <button disabled={!hasNext} onClick={onNext} className={`${hasNext ? 'text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800' : 'text-slate-400 cursor-not-allowed'} flex items-center gap-2 px-4 py-2 rounded-md`} aria-disabled={!hasNext}>
          <span>Next</span>
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

function AdminLogin({ onSuccess }: { onSuccess: () => void }) {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const login = async () => {
    setError('');
    const res = await fetch(`${API_BASE_URL}/api/blog/admin/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ password }),
    });
    if (res.ok) onSuccess(); else setError('Invalid password');
  };
  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="max-w-md mx-auto px-4 py-10">
      <div className="relative group">
        <div className="absolute -inset-1 bg-gradient-to-r from-indigo-500 to-violet-500 rounded-2xl blur opacity-20 group-hover:opacity-30 transition duration-700" />
        <div className="relative rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <Lock className="w-5 h-5 text-slate-500" />
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Admin Login</h3>
          </div>
          <div className="space-y-4">
            <input
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="Admin password"
              className="w-full rounded-lg border border-slate-200 dark:border-slate-800 bg-transparent px-3 py-2 outline-none text-slate-800 dark:text-slate-200 placeholder-slate-400"
            />
            <button onClick={login} className="inline-flex items-center justify-center gap-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 font-medium transition">
              Login
            </button>
            {error && <div className="text-red-600 text-sm">{error}</div>}
          </div>
        </div>
      </div>
    </motion.div>
  );
}

function AdminDashboard({ lang, posts, setPosts, onEdit }: { lang: Lang; posts: Post[]; setPosts: (v: Post[]) => void; onEdit: (p: Post) => void }) {
  useEffect(() => {
    const refresh = async () => {
      const res = await fetch(`${API_BASE_URL}/api/blog/posts?status_filter=published&lang=${lang}`, { credentials: 'include' });
      if (res.ok) setPosts((await res.json()).map(normalizePostFromAPI));
    };
    refresh();
  }, [lang, setPosts]);
  const del = async (id?: number) => {
    if (!id) return;
    const res = await fetch(`${API_BASE_URL}/api/blog/posts/${id}`, { method: 'DELETE', credentials: 'include' });
    if (res.ok) setPosts(posts.filter(p => p.id !== id));
  };
  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="max-w-5xl mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-2xl font-bold text-slate-900 dark:text-white">Admin Dashboard</h3>
        <button onClick={() => onEdit({ title: '', content: '', status: 'draft', language: lang })} className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 font-medium">
          <Plus className="w-4 h-4" /> New Post
        </button>
      </div>
      <div className="overflow-hidden rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 shadow-sm">
        <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-800">
          <thead className="bg-slate-50 dark:bg-slate-900">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300">Title</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300">Lang</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300">Status</th>
              <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 dark:text-slate-300">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
            {posts.map(p => (
              <tr key={p.id} className="hover:bg-slate-50 dark:hover:bg-slate-900">
                <td className="px-4 py-3 text-sm text-slate-800 dark:text-slate-200">{p.title}</td>
                <td className="px-4 py-3 text-sm text-slate-500 dark:text-slate-400">{lang}</td>
                <td className="px-4 py-3 text-sm">
                  <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300">
                    {p.status}
                  </span>
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="flex justify-end gap-2">
                    <button onClick={() => onEdit(p)} className="inline-flex items-center gap-2 rounded-md border border-slate-200 dark:border-slate-700 px-3 py-1.5 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-900">
                      <Pencil className="w-4 h-4" /> Edit
                    </button>
                    <button onClick={() => del(p.id)} className="inline-flex items-center gap-2 rounded-md border border-rose-300 text-rose-600 px-3 py-1.5 hover:bg-rose-50">
                      <Trash2 className="w-4 h-4" /> Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </motion.div>
  );
}

function AdminEditor({ post, onCancel, onSaved }: { post: Post; onCancel: () => void; onSaved: (p: Post) => void }) {
  const [title, setTitle] = useState(post.title || '');
  const [content, setContent] = useState(post.content || '');
  const [status, setStatus] = useState<'draft'|'published'>(post.status || 'draft');
  const [saving, setSaving] = useState(false);

  // Translations state keyed by language code
  const [translations, setTranslations] = useState<Record<string, { title: string; content: string }>>(() => post.translations || {});
  const allLangs = useMemo(() => SUPPORTED_LANGS.filter(l => l !== 'en'), []);
  const [enabledLangs, setEnabledLangs] = useState<string[]>(() => Object.keys(translations));

  const contentRef = useRef<HTMLTextAreaElement>(null);
  const translationRefs = useRef<Record<string, HTMLTextAreaElement | null>>({});

  const toggleLang = (code: string, checked: boolean) => {
    setEnabledLangs(prev => {
      if (checked) return prev.includes(code) ? prev : [...prev, code];
      return prev.filter(l => l !== code);
    });
    setTranslations(prev => {
      const next = { ...prev };
      if (checked && !next[code]) next[code] = { title: '', content: '' };
      if (!checked) delete next[code];
      return next;
    });
  };

  const setTranslationField = (code: string, field: 'title'|'content', value: string) => {
    setTranslations(prev => ({ ...prev, [code]: { ...prev[code], [field]: value } }));
  };

  const applyFormat = (ref: HTMLTextAreaElement | null, setValue: (v: string) => void, startTag: string, endTag: string) => {
    if (!ref) return;
    const el = ref;
    const start = el.selectionStart ?? 0;
    const end = el.selectionEnd ?? 0;
    const v = el.value || '';
    const selected = v.slice(start, end);
    const wrapped = `${v.slice(0,start)}${startTag}${selected || 'text'}${endTag}${v.slice(end)}`;
    setValue(wrapped);
    setTimeout(() => {
      el.focus();
      el.selectionStart = start + startTag.length;
      el.selectionEnd = start + startTag.length + (selected || 'text').length;
    }, 0);
  };

  const uploadImage = async (file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    const res = await fetch(`${API_BASE_URL}/api/blog/media/upload`, { method: 'POST', body: fd, credentials: 'include' });
    if (res.ok) {
      const data = await res.json();
      setContent(prev => `${prev}\n\n<img src="${data.url}" alt="image" />`);
    }
  };

  const save = async () => {
    setSaving(true);

    const translationsArr = enabledLangs
      .map(code => ({ code, data: translations[code] }))
      .filter(x => x.data && x.data.title?.trim() && x.data.content?.trim())
      .map(x => ({ language_code: x.code, title: x.data.title.trim(), content: x.data.content.trim() }));

    const isUpdate = Boolean(post.id);
    const url = isUpdate ? `${API_BASE_URL}/api/blog/posts/${post.id}` : `${API_BASE_URL}/api/blog/posts`;

    const payload: any = isUpdate
      ? { title, content, status, translations: translationsArr }
      : {
          slug: post.slug || title.trim().toLowerCase().replace(/[^a-z0-9]+/g,'-').replace(/^-|-$|--+/g,'-'),
          title,
          content,
          status,
          author: 'admin',
          translations: translationsArr,
        };

    const res = await fetch(url, {
      method: isUpdate ? 'PUT' : 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload),
    });
    setSaving(false);
    if (res.ok) onSaved(normalizePostFromAPI(await res.json()));
  };

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="max-w-3xl mx-auto px-4 py-8">
      <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-6">{post.id ? 'Edit Post' : 'New Post'}</h3>
      <div className="space-y-5">
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Title (English)</label>
          <input value={title} onChange={e => setTitle(e.target.value)} placeholder="Title" className="w-full rounded-lg border border-slate-200 dark:border-slate-800 bg-transparent px-3 py-2 outline-none" />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Content (English)</label>
          <div className="flex gap-2 mb-2">
            <button type="button" onClick={() => applyFormat(contentRef.current, setContent, '<strong>', '</strong>')} className="px-2 py-1 text-xs border rounded">Bold</button>
            <button type="button" onClick={() => applyFormat(contentRef.current, setContent, '<em>', '</em>')} className="px-2 py-1 text-xs border rounded">Italic</button>
            <button type="button" onClick={() => applyFormat(contentRef.current, setContent, '<u>', '</u>')} className="px-2 py-1 text-xs border rounded">Underline</button>
          </div>
          <textarea ref={contentRef} value={content} onChange={e => setContent(e.target.value)} placeholder="HTML or text" className="w-full h-56 rounded-lg border border-slate-200 dark:border-slate-800 bg-transparent px-3 py-2 outline-none" />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Translations</label>
          <div className="flex flex-wrap gap-3">
            {allLangs.map(code => (
              <label key={code} className="inline-flex items-center gap-2">
                <input type="checkbox" checked={enabledLangs.includes(code)} onChange={e => toggleLang(code, e.target.checked)} />
                <span>{LANG_LABELS[code] || code}</span>
              </label>
            ))}
          </div>
          <p className="text-xs text-slate-500 mt-2">If a translation is unchecked or left blank, visitors will see English.</p>
        </div>

        {enabledLangs.map(code => (
          <div key={code} className="rounded-lg border border-slate-200 dark:border-slate-800 p-3">
            <div className="font-medium mb-2">Translation: {LANG_LABELS[code] || code}</div>
            <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Title ({code})</label>
            <input value={translations[code]?.title || ''} onChange={e => setTranslationField(code, 'title', e.target.value)} placeholder={`Title (${code})`} className="w-full rounded-lg border border-slate-200 dark:border-slate-800 bg-transparent px-3 py-2 outline-none mb-2" />
            <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Content ({code})</label>
            <div className="flex gap-2 mb-2">
              <button type="button" onClick={() => applyFormat(translationRefs.current[code] || null, v => setTranslationField(code, 'content', v), '<strong>', '</strong>')} className="px-2 py-1 text-xs border rounded">Bold</button>
              <button type="button" onClick={() => applyFormat(translationRefs.current[code] || null, v => setTranslationField(code, 'content', v), '<em>', '</em>')} className="px-2 py-1 text-xs border rounded">Italic</button>
              <button type="button" onClick={() => applyFormat(translationRefs.current[code] || null, v => setTranslationField(code, 'content', v), '<u>', '</u>')} className="px-2 py-1 text-xs border rounded">Underline</button>
            </div>
            <textarea ref={el => { translationRefs.current[code] = el; }} value={translations[code]?.content || ''} onChange={e => setTranslationField(code, 'content', e.target.value)} placeholder={`HTML or text (${code})`} className="w-full h-40 rounded-lg border border-slate-200 dark:border-slate-800 bg-transparent px-3 py-2 outline-none" />
          </div>
        ))}

        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Upload Image</label>
          <input type="file" accept="image/*" onChange={e => { const f = e.target.files?.[0]; if (f) uploadImage(f); }} className="w-full rounded-lg border border-slate-200 dark:border-slate-800 bg-transparent px-3 py-2 outline-none" />
        </div>
        <div className="flex items-center gap-3">
          <select value={status} onChange={e => setStatus(e.target.value as any)} className="rounded-lg border border-slate-200 dark:border-slate-800 bg-transparent px-3 py-2 outline-none">
            <option value="draft">Draft</option>
            <option value="published">Published</option>
          </select>
          <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 font-medium disabled:opacity-60">
            {saving ? (<Loader2 className="w-4 h-4 animate-spin" />) : (<Save className="w-4 h-4" />)}
            {saving ? 'Saving...' : 'Save'}
          </button>
          <button onClick={onCancel} className="inline-flex items-center gap-2 rounded-lg border border-slate-200 dark:border-slate-700 px-4 py-2 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-900">Cancel</button>
        </div>
      </div>
    </motion.div>
  );
}
